import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../components/Modal';
import { SmsCodeButton } from '../components/auth/SmsCodeButton';
import { useAuth } from './AuthContext';
import { authApi } from '../services/auth';
import { PRIVACY_POLICY_VERSION } from '../config/policy';

interface PhoneVerificationContextValue {
  /**
   * 登录用户手机已验证 → 直接执行 action；
   * 否则弹出绑定手机模态框，绑定成功后自动继续执行原 action。
   */
  requirePhoneVerified: (action: () => void) => void;
  /**
   * 手机验证开关开 && 已登录 && 手机未验证 → true；否则 false。
   * 供各处按钮文案切换使用（true 时改为「验证后…」）。
   */
  needsVerify: boolean;
}

const PhoneVerificationContext = createContext<PhoneVerificationContextValue | null>(null);

export function PhoneVerificationModalProvider({ children }: { children: ReactNode }) {
  const { user, updateUser, phoneVerifyEnabled } = useAuth();
  const needsVerify = !!(phoneVerifyEnabled && user && !user.phoneVerified);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingActionRef = useRef<null | (() => void)>(null);

  const resetForm = useCallback(() => {
    setPhone('');
    setCode('');
    setAgreed(false);
    setLoading(false);
    setError(null);
  }, []);

  // 关闭并清空表单（不改动 pendingActionRef —— 提交/取消各自负责）。
  const close = useCallback(() => {
    setOpen(false);
    resetForm();
  }, [resetForm]);

  const requirePhoneVerified = useCallback(
    (action: () => void) => {
      // 手机强制验证总开关关时（默认），后端 gate 也放行 —— 前端直接执行，不弹绑定框。
      if (!phoneVerifyEnabled) {
        action();
        return;
      }
      if (user?.phoneVerified) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setOpen(true);
    },
    [phoneVerifyEnabled, user?.phoneVerified],
  );

  // 全局 'zx:phone-required' 事件（后端 403 PHONE_VERIFICATION_REQUIRED 触发）→ 纯提示弹框，不带待续动作。
  useEffect(() => {
    function onRequired() {
      setOpen(true);
    }
    window.addEventListener('zx:phone-required', onRequired);
    return () => {
      window.removeEventListener('zx:phone-required', onRequired);
    };
  }, []);

  const cancel = useCallback(() => {
    pendingActionRef.current = null;
    close();
  }, [close]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);
      try {
        const res = await authApi.bindPhone({
          phone,
          code,
          acceptPrivacy: agreed,
          privacyVersion: PRIVACY_POLICY_VERSION,
        });
        updateUser(res.data.user);
        const act = pendingActionRef.current;
        pendingActionRef.current = null;
        close();
        act?.();
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '绑定失败，请重试';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [phone, code, agreed, updateUser, close],
  );

  const value = useMemo<PhoneVerificationContextValue>(
    () => ({ requirePhoneVerified, needsVerify }),
    [requirePhoneVerified, needsVerify],
  );

  return (
    <PhoneVerificationContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onClose={cancel}
        title="验证手机号"
        size="md"
        dismissible={false}
      >
        <form className="space-y-4" onSubmit={submit}>
          <p className="text-sm leading-relaxed text-fg-muted">
            应监管要求，需验证并绑定手机号后方可继续使用。详见
            <Link to="/privacy" target="_blank" className="text-navy hover:underline">
              《隐私政策》
            </Link>
            。
          </p>

          <div>
            <label className="block text-sm font-medium text-fg-muted">手机号</label>
            <input
              type="tel"
              required
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-fg-muted">验证码</label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6 位验证码"
                className="w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <SmsCodeButton phone={phone} purpose="bind" onError={setError} />
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-fg-muted">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-navy"
            />
            <span>
              我已阅读并接受
              <Link to="/privacy" target="_blank" className="text-navy hover:underline">
                《隐私政策》
              </Link>
            </span>
          </label>

          {error && (
            <div className="rounded bg-danger-tint p-3 text-sm text-danger max-lg:rounded-chip">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={cancel}
              className="flex-1 rounded border border-line px-4 py-2.5 text-sm font-medium text-fg-muted hover:border-navy hover:text-fg max-lg:rounded-control"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading || !agreed}
              className="flex-1 rounded bg-navy-fill px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-fill-strong disabled:opacity-50 max-lg:rounded-control"
            >
              {loading ? '绑定中…' : '验证并绑定'}
            </button>
          </div>
        </form>
      </Modal>
    </PhoneVerificationContext.Provider>
  );
}

export function usePhoneVerification() {
  const ctx = useContext(PhoneVerificationContext);
  if (!ctx) {
    throw new Error('usePhoneVerification must be used inside <PhoneVerificationModalProvider>');
  }
  return ctx;
}
